/* eslint-disable no-console */
/**
 * Phase 2 — offscreen-side orchestrator for view-only scan workers.
 *
 * Owns the lifecycle of one scan worker per routed chain (Phase 3: ETH/ARB/BSC,
 * see RAILGUN_SCAN_WORKER_CHAIN_IDS). Each worker holds a view-only engine in its
 * own realm + IndexedDB and scans that chain off the main thread. The
 * orchestrator:
 *   - spawns + inits a worker on first use (provider/POI config computed here on
 *     the main thread, where the ambire-network settings live, and shipped in);
 *   - hands the worker the canonical wallet's shareableViewingKey so it can
 *     create a VIEW-ONLY wallet (spending key never crosses into the worker);
 *   - on each scan, remaps the worker's view-only walletId back to the canonical
 *     id and re-emits balance/scan/POI events onto the normal railgunEmitter,
 *     so the rest of the offscreen + SW + UI pipeline is unchanged;
 *   - resolves the caller's promise when the worker finishes its scan.
 *
 * `refreshBalancesMaybeWorker` is the single seam: RailgunWallet.getBalance calls
 * it instead of `refreshBalances(chain, ...)` directly. It returns true when the
 * worker handled the chain; false (or a thrown error) tells the caller to fall
 * back to a normal main-thread engine scan — so a worker failure can never lose
 * a balance, only the parallelism.
 *
 * See extension/docs/railgun-parallel-scan.md §4 + Phase 2 spec.
 */
import { emitRailgunEvent } from '@web/utils/railgunListener'
import { relabelChainAfterWorkerScan } from '@web/utils/railgunRelabel'
import { syncProfiler } from '@web/utils/railgunSyncProfiler'
import {
  POLLING_INTERVAL_MS,
  RAILGUN_DB_NAME_BACKGROUND,
  RAILGUN_DB_NAME_UI,
  RAILGUN_ARTIFACT_DB_NAME_UI,
  RAILGUN_SHARED_SCAN_DB,
  RAILGUN_SCAN_WORKER_CHAIN_IDS,
  getNetworkNameForChainId,
  getNetworkRpcConfig,
  getPoiNodeUrlsForEngine
} from '@web/utils/railgunCore'
import { spawnScanWorker } from '@web/extension-services/offscreen/scanWorker/spawnScanWorker'
import { POIProofEventStatus } from '@railgun-community/shared-models'
import type { Chain, NetworkName } from '@railgun-community/shared-models'
import type {
  ScanWorkerInbound,
  ScanWorkerOutbound
} from '@web/extension-services/offscreen/scanWorker/railgunScanWorkerMessages'

type RefreshContext = {
  encryptionKey: string
  getShareableViewingKey: () => Promise<string>
}

type ChainWorker = {
  worker: Worker
  initPromise: Promise<void>
  pendingRefresh: Map<string, { resolve: () => void; reject: (e: Error) => void }>
}

const workers: Map<number, ChainWorker> = new Map()
// chainId -> number of active suspensions. Ref-counted because a full rescan and
// a proof can both hold this chain still at the same time; the worker must only
// come back when the LAST holder releases. See suspendScanWorkerForChain.
const suspensionsByChain: Map<number, number> = new Map()
// viewOnlyWalletId -> canonicalWalletId, for event remap.
const canonicalByViewOnly: Map<string, string> = new Map()
// canonicalWalletId -> last refresh ctx, so the POI-complete hook can re-run
// relabel without a fresh getBalance call (it needs encryptionKey + the resolved
// shareableViewingKey to address the view-only wallet).
const relabelCtxByCanonical: Map<string, { encryptionKey: string; shareableViewingKey: string }> =
  new Map()
// Per-chain guard so overlapping AllProofsCompleted events don't stack relabels.
const poiRelabelInFlight: Set<number> = new Set()
// Canonical id of the wallet workers are currently scanning. A change = account
// switch → unload the previous wallet from every worker so its view-only wallet
// stops occupying engine memory (Phase 4).
let activeCanonicalWalletId: string | null = null

let requestSeq = 0
const nextRequestId = (): string => `scanreq-${Date.now()}-${requestSeq++}`

/**
 * Extension host = the orchestrator runs in the offscreen document, where
 * `chrome.runtime` exists. Mobile-web host = it runs inside the railgun worker
 * (a Web Worker, no `chrome`). The two hosts boot their local engine against
 * DIFFERENT IndexedDB names (extension: BACKGROUND; mobile-web: UI default) and
 * ship artifacts differently (extension: bundled; mobile-web: IPFS-fetched), so
 * the scan workers must mirror whichever host spawned them — otherwise the
 * relabel writes into a DB the local engine never reads, and balances stay 0.
 */
const isExtensionHost =
  typeof chrome !== 'undefined' &&
  !!chrome.runtime &&
  typeof chrome.runtime.getURL === 'function'

// Merkletree IDB the LOCAL engine uses — workers must scan into the same store so
// `relabelChainAfterWorkerScan` (which runs in the local engine) finds the notes.
const LOCAL_MERKLETREE_DB = isExtensionHost ? RAILGUN_DB_NAME_BACKGROUND : RAILGUN_DB_NAME_UI

function canSpawnWorker(): boolean {
  // Both hosts have the Worker constructor (mobile-web spawns a nested worker
  // from inside the railgun worker — Chrome supports nested dedicated workers).
  return typeof Worker === 'function'
}

function post(worker: Worker, msg: ScanWorkerInbound): void {
  worker.postMessage(msg)
}

/**
 * Record the wallet now being scanned. On a change (account switch), tell every
 * worker to unload the previous canonical wallet and drop its remap entries.
 * Idempotent for the concurrent per-chain calls of one wallet (same id → no-op).
 */
function noteActiveWallet(canonicalWalletId: string): void {
  if (activeCanonicalWalletId === canonicalWalletId) return
  const stale = activeCanonicalWalletId
  activeCanonicalWalletId = canonicalWalletId
  if (!stale) return
  console.log('[ScanWorker] account switch — unloading stale view-only wallet from workers', {
    stale,
    nowActive: canonicalWalletId,
    workers: workers.size
  })
  for (const cw of workers.values()) {
    post(cw.worker, { type: 'UNLOAD_WALLET', params: { canonicalWalletId: stale } })
  }
  for (const [viewOnly, canonical] of canonicalByViewOnly) {
    if (canonical === stale) canonicalByViewOnly.delete(viewOnly)
  }
  relabelCtxByCanonical.delete(stale)
}

/**
 * Re-copy a chain's view-only namespace onto the canonical wallet + invalidate its
 * commitment cache after the worker's POI proofs complete, so spendable balance
 * updates without a manual refresh/reload. Best-effort; per-chain in-flight guarded.
 */
function relabelAfterPoiComplete(chain: Chain, canonicalWalletId: string): Promise<void> {
  const ctx = relabelCtxByCanonical.get(canonicalWalletId)
  if (!ctx) return Promise.resolve()
  if (poiRelabelInFlight.has(chain.id)) return Promise.resolve()
  poiRelabelInFlight.add(chain.id)
  return relabelChainAfterWorkerScan(
    chain,
    canonicalWalletId,
    ctx.encryptionKey,
    ctx.shareableViewingKey
  )
    .then(() => {
      console.log('[ScanWorker] POI complete → re-relabeled + invalidated', chain.id)
    })
    .catch((err) => {
      console.warn('[ScanWorker] POI-complete relabel failed', chain.id, err)
    })
    .finally(() => {
      poiRelabelInFlight.delete(chain.id)
    })
}

/** Remap a worker event's view-only walletId to the canonical id, then re-emit. */
function handleWorkerMessage(chainId: number, msg: ScanWorkerOutbound): void {
  const cw = workers.get(chainId)
  switch (msg.type) {
    case 'WALLET_MAPPED':
      canonicalByViewOnly.set(msg.viewOnlyWalletId, msg.canonicalWalletId)
      break
    case 'BALANCE_UPDATE': {
      const canonical = canonicalByViewOnly.get(msg.event.railgunWalletID)
      // Rewrite the id so getBalance's onEvent (keyed by canonical walletId) and
      // the SW forwarders see it as the canonical wallet's balance.
      const event = canonical ? { ...msg.event, railgunWalletID: canonical } : msg.event
      emitRailgunEvent('balanceUpdate', event)
      break
    }
    case 'UTXO_SCAN':
      // The worker's engine fires its scan callbacks in the worker realm, so the
      // main-thread syncProfiler (fed inline from railgunCore's callbacks) never
      // sees this chain. Feed it here too, or the Phase 3 `wallClock ≪
      // summedUtxoScan` parallelism check would undercount worker-scanned chains.
      syncProfiler.utxoScan(msg.event)
      emitRailgunEvent('utxoScanUpdate', msg.event)
      break
    case 'TXID_SCAN':
      syncProfiler.txidScan(msg.event)
      emitRailgunEvent('txidScanUpdate', msg.event)
      break
    case 'POI_PROOF':
      emitRailgunEvent('poiProofProgress', msg.event)
      // When a worker chain finishes all POI proofs, its view-only namespace now
      // holds the completed POI; re-copy it onto the canonical wallet + invalidate
      // so the spendable balance reflects it WITHOUT a manual refresh/reload. The
      // scan-time relabel ran before POI settled, so canonical otherwise keeps the
      // pre-POI copy until the next getBalance — which may never come once the
      // balance loop idles (the BSC "stuck pending until reload" case).
      if (RAILGUN_SHARED_SCAN_DB && msg.event.status === POIProofEventStatus.AllProofsCompleted) {
        const canonical = canonicalByViewOnly.get(msg.event.railgunWalletID)
        if (canonical) void relabelAfterPoiComplete(msg.event.chain, canonical)
      }
      break
    case 'BATCH_LIST':
      syncProfiler.poiBatch(msg.event as unknown as { chain?: { id: number } })
      emitRailgunEvent('batchListUpdate', msg.event)
      break
    case 'REFRESH_DONE': {
      const pending = cw?.pendingRefresh.get(msg.requestId)
      if (pending) {
        cw!.pendingRefresh.delete(msg.requestId)
        pending.resolve()
      }
      break
    }
    case 'REFRESH_ERROR': {
      const pending = cw?.pendingRefresh.get(msg.requestId)
      if (pending) {
        cw!.pendingRefresh.delete(msg.requestId)
        pending.reject(new Error(msg.error))
      }
      break
    }
    case 'INIT_DONE':
    case 'INIT_ERROR':
      // Handled by the initPromise wired in getOrSpawnWorker.
      break
    default:
      break
  }
}

function getOrSpawnWorker(chainId: number, networkName: NetworkName): ChainWorker {
  const existing = workers.get(chainId)
  if (existing) return existing

  const worker = spawnScanWorker(chainId)
  const pendingRefresh = new Map<string, { resolve: () => void; reject: (e: Error) => void }>()

  let initResolve!: () => void
  let initReject!: (e: Error) => void
  const initPromise = new Promise<void>((res, rej) => {
    initResolve = res
    initReject = rej
  })

  worker.addEventListener('message', (e: MessageEvent<ScanWorkerOutbound>) => {
    const msg = e.data
    if (msg.type === 'INIT_DONE') initResolve()
    else if (msg.type === 'INIT_ERROR') initReject(new Error(msg.error))
    handleWorkerMessage(chainId, msg)
  })
  worker.addEventListener('error', (err) => {
    const e = new Error(`scan worker ${chainId} crashed: ${err.message}`)
    initReject(e)
    for (const [id, p] of pendingRefresh) {
      pendingRefresh.delete(id)
      p.reject(e)
    }
    // Drop the dead worker so the next call respawns it.
    workers.delete(chainId)
  })

  const config = getNetworkRpcConfig(networkName)
  const init: ScanWorkerInbound = {
    type: 'INIT',
    params: {
      networkName,
      providerConfig: {
        chainId: config.chainId,
        providers: config.providers,
        pollingInterval: POLLING_INTERVAL_MS
      },
      poiNodeURLs: getPoiNodeUrlsForEngine(),
      // P1 POC: when the shared-DB flag is on, every worker + the main engine
      // open ONE shared DB to test multi-writer safety; otherwise keep per-chain
      // DB isolation. The base name is the LOCAL engine's merkletree DB so the
      // post-scan relabel (run in the local engine) finds the notes.
      dbName: RAILGUN_SHARED_SCAN_DB
        ? LOCAL_MERKLETREE_DB
        : `${LOCAL_MERKLETREE_DB}-${chainId}`,
      // Extension ships bundled artifacts; mobile-web fetches them from IPFS into
      // the same IndexedDB store its main engine uses (so zkeys are shared, not
      // re-downloaded per realm).
      useBundledArtifacts: isExtensionHost,
      artifactDbName: RAILGUN_ARTIFACT_DB_NAME_UI
    }
  }
  post(worker, init)
  console.log('[ScanWorker] spawned + init', {
    chainId,
    networkName,
    scanProvider: config.providers[0]?.provider
  })

  const cw: ChainWorker = { worker, initPromise, pendingRefresh }
  workers.set(chainId, cw)
  return cw
}

/**
 * One-time cleanup of the per-chain scan DBs that the pre-D2 path created
 * (`zkx-railgun-db-sw-<chainId>`, stored as `level-js-zkx-railgun-db-sw-<chainId>`).
 * Under D2 (`RAILGUN_SHARED_SCAN_DB`) every worker uses the shared base DB, so the
 * per-chain DBs are never opened and are dead storage — delete them. Safe only while
 * the shared path is active (no worker holds a per-chain DB open). No-op when the flag
 * is off, when `indexedDB.databases()` is unavailable, or on any per-DB delete error
 * (best-effort: leftover storage is harmless, a thrown delete must not block init).
 */
export async function cleanupOrphanPerChainScanDbs(): Promise<void> {
  if (!RAILGUN_SHARED_SCAN_DB) return
  const idb = (globalThis as any).indexedDB
  if (!idb || typeof idb.databases !== 'function') return
  // Matches `level-js-<base>-<digits>` exactly — never the shared base DB itself.
  const orphanRe = new RegExp(`^level-js-${RAILGUN_DB_NAME_BACKGROUND}-\\d+$`)
  try {
    const dbs: Array<{ name?: string }> = await idb.databases()
    const names = dbs.map((d) => d.name).filter((n): n is string => !!n && orphanRe.test(n))
    for (const name of names) {
      try {
        await new Promise<void>((resolve) => {
          const req = idb.deleteDatabase(name)
          req.onsuccess = () => resolve()
          req.onerror = () => resolve()
          req.onblocked = () => resolve()
        })
        console.log('[ScanWorker] deleted orphan per-chain DB', name)
      } catch (err) {
        console.warn('[ScanWorker] orphan DB delete failed', name, err)
      }
    }
  } catch (err) {
    console.warn('[ScanWorker] cleanupOrphanPerChainScanDbs failed', err)
  }
}

/**
 * Route a single chain's balance refresh to its view-only scan worker.
 *
 * @returns true when the worker handled the scan (caller must NOT also scan on
 *   the main engine); false when this chain isn't routed to a worker. Throws if
 *   the worker path was attempted but failed — RailgunWallet.getBalance catches
 *   that and falls back to a main-thread scan.
 */
export async function refreshBalancesMaybeWorker(
  chain: { type: number; id: number },
  canonicalWalletId: string,
  ctx: RefreshContext
): Promise<boolean> {
  if (!RAILGUN_SCAN_WORKER_CHAIN_IDS.has(chain.id)) return false
  // A full rescan owns this chain's namespace right now — fall back to the main
  // engine, which serializes against that rescan via its own `isScanning` flag.
  if (suspensionsByChain.has(chain.id)) return false
  if (!canSpawnWorker()) return false

  const networkName = getNetworkNameForChainId(chain.id)
  if (!networkName) return false

  noteActiveWallet(canonicalWalletId)
  const cw = getOrSpawnWorker(chain.id, networkName)
  await cw.initPromise

  const shareableViewingKey = await ctx.getShareableViewingKey()
  // Remember the keys so the POI-complete hook can re-relabel later without a
  // fresh getBalance (it fires off a worker event, outside this call's scope).
  relabelCtxByCanonical.set(canonicalWalletId, {
    encryptionKey: ctx.encryptionKey,
    shareableViewingKey
  })
  const requestId = nextRequestId()
  await new Promise<void>((resolve, reject) => {
    cw.pendingRefresh.set(requestId, { resolve, reject })
    post(cw.worker, {
      type: 'REFRESH_BALANCES',
      params: {
        requestId,
        canonicalWalletId,
        encryptionKey: ctx.encryptionKey,
        shareableViewingKey
      }
    })
  })

  // P2: the worker wrote notes under its VIEW-ONLY id. Copy that chain's
  // wallet-scoped namespace onto the canonical (spend) id in the shared DB so
  // gas estimation / proof generation on the main engine sees the notes. No-op
  // unless RAILGUN_SHARED_SCAN_DB is on. A relabel failure must not lose the
  // balance the worker already surfaced — swallow and let the UI keep it.
  if (RAILGUN_SHARED_SCAN_DB) {
    try {
      await relabelChainAfterWorkerScan(chain, canonicalWalletId, ctx.encryptionKey, shareableViewingKey)
    } catch (err) {
      console.warn('[ScanWorker] relabel failed (shared-DB POC)', chain.id, err)
    }
  }

  return true
}

/**
 * Park this chain's scan worker for the duration of a DESTRUCTIVE main-engine
 * operation (a full UTXO rescan, which wipes and rebuilds the chain's namespace).
 *
 * Under D2 (`RAILGUN_SHARED_SCAN_DB`) every scan worker and the main engine open
 * ONE shared IndexedDB. The SDK's `utxoMerkletree.isScanning` guard only
 * serializes scans WITHIN a single realm, so it cannot stop a per-chain scan
 * worker in another realm from appending leaves while the main engine is clearing
 * that same namespace — an interleave that would leave exactly the inconsistent
 * tree the rescan is meant to repair. Terminating the worker and refusing to
 * respawn it is the only cross-realm interlock available.
 *
 * While suspended, `refreshBalancesMaybeWorker` reports "not routed", so
 * `RailgunWallet.getBalance` falls back to a main-engine scan — which the engine's
 * own `isScanning` flag correctly serializes against the in-flight rescan.
 */
export function suspendScanWorkerForChain(chainId: number): void {
  suspensionsByChain.set(chainId, (suspensionsByChain.get(chainId) ?? 0) + 1)
  const cw = workers.get(chainId)
  if (!cw) return
  workers.delete(chainId)
  try {
    cw.worker.terminate()
  } catch {
    /* already dead */
  }
  // The terminated worker can never answer these — reject rather than leave the
  // caller hanging until its own timeout.
  for (const [id, pending] of cw.pendingRefresh) {
    cw.pendingRefresh.delete(id)
    pending.reject(new Error(`scan worker ${chainId} suspended for full rescan`))
  }
}

/**
 * Undo one {@link suspendScanWorkerForChain}. The worker only becomes eligible to
 * respawn once every holder has released.
 */
export function resumeScanWorkerForChain(chainId: number): void {
  const remaining = (suspensionsByChain.get(chainId) ?? 0) - 1
  if (remaining > 0) suspensionsByChain.set(chainId, remaining)
  else suspensionsByChain.delete(chainId)
}

/** Tear down all scan workers (e.g. on engine stop / lock). */
export function stopAllScanWorkers(): void {
  for (const [chainId, cw] of workers) {
    try {
      cw.worker.terminate()
    } catch {
      /* ignore */
    }
    workers.delete(chainId)
  }
  canonicalByViewOnly.clear()
  relabelCtxByCanonical.clear()
  poiRelabelInFlight.clear()
  activeCanonicalWalletId = null
}
